import { UseChatHelpers } from '@ai-sdk/react';
import { LlmUIMessage as LlmChatMessage } from '@azymmetric/shared/types/llm.types';
import { LlmModelSelector } from '@components/shared/llm-model-selector.component';
import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea';
import { useScrollToBottom } from '@hooks/use-scroll-to-bottom.hook';
import { cn } from '@lib/utils';
import { Send, Square } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react';

export type ChatInputProps = {
  chatId: string;
  className?: string;
  input: string;
  messages: LlmChatMessage[];
  sendMessage: UseChatHelpers<LlmChatMessage>['sendMessage'];
  setInput: Dispatch<SetStateAction<string>>;
  status: UseChatHelpers<LlmChatMessage>['status'];
  stop: UseChatHelpers<LlmChatMessage>['stop'];
};

export const ChatInput: React.FC<ChatInputProps> = (props) => {
  const { input, setInput, chatId, sendMessage, status, stop } = props;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (input.trim() && status !== 'streaming' && status !== 'submitted') {
        submitForm();
      }
    }
  };

  const submitForm = useCallback(() => {
    window.history.replaceState({}, '', `/chat/${chatId}`);

    sendMessage({
      role: 'user',
      parts: [
        {
          type: 'text',
          text: input,
        },
      ],
    });

    setInput('');
    textareaRef.current?.focus();
  }, [input, setInput, sendMessage, chatId]);

  const { scrollToBottom } = useScrollToBottom();

  useEffect(() => {
    if (status === 'submitted') {
      scrollToBottom();
    }
  }, [status, scrollToBottom]);

  return (
    <section
      className="m-2 w-full cursor-text rounded-xl border border-gray-200 bg-gray-100 focus-within:border-gray-500"
      onClick={() => textareaRef.current?.focus()}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (input.trim() && status !== 'streaming' && status !== 'submitted') {
            submitForm();
          }
        }}
      >
        <Textarea
          className={cn(
            'resize-none border-0 bg-transparent focus:border-0 focus:ring-0',
            'focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0',
            'placeholder:text-gray-400',
          )}
          ref={textareaRef}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type your message here..."
          rows={3}
          value={input}
        />

        <div className="flex items-end justify-between p-1">
          <div className="flex items-center gap-1">
            <LlmModelSelector showCapabilityIcons />
          </div>
          {status === 'submitted' || status === 'streaming' ? (
            <Button
              onClick={stop}
              variant="secondary"
              size="icon"
            >
              <Square className="size-4" />
            </Button>
          ) : (
            <Button
              disabled={!input.trim()}
              size="icon"
              type="submit"
            >
              <Send className="size-4" />
            </Button>
          )}
        </div>
      </form>
    </section>
  );
};
